import React from 'react';
import { motion } from 'framer-motion';
import { XCircleIcon, SparklesIcon, CalendarIcon, UsersIcon, ChatBubbleLeftRightIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const DeclinedPage: React.FC = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const isPortalUser = queryParams.get('isPortalUser') === 'true';

  const handleRedirect = () => {
    window.location.href = isPortalUser ? '/login' : '/signup';
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col justify-center py-12 sm:px-6 lg:px-8 px-4">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mb-6"
        >
          <XCircleIcon className="h-10 w-10" />
        </motion.div>
        <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight sm:text-4xl">
          Request Declined
        </h2>
        <p className="mt-3 text-base text-slate-500 dark:text-slate-400">
          {isPortalUser 
            ? "You have declined the CRM lead link request. The creator has been notified." 
            : "You have declined the lead request."}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-slate-900 py-8 px-4 shadow-xl rounded-2xl sm:px-10 border border-slate-100 dark:border-slate-800"
        >
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                {isPortalUser ? "Go to Portal Dashboard" : "Checkout ClickCollab"}
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {isPortalUser 
                  ? "You can still access your client portal to view other active shoots, messages, or details." 
                  : "ClickCollab is completely free for everyone. Explore what you can build and organize on the platform!"}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 py-4">
              {isPortalUser ? (
                <>
                  <div className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <CalendarIcon className="h-6 w-6 text-primary flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Manage Bookings & Shoots</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">View active proposals, contracts, and shoot schedules in one central dashboard.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-primary flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Direct Messaging</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Chat directly with your photographer or visual creator in real time.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <DocumentTextIcon className="h-6 w-6 text-primary flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Invoices & Receipts</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Review digital invoices, retainer payments, and receipts securely.</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <SparklesIcon className="h-6 w-6 text-primary flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">100% Free Collaborations</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Scale your networking and team shoots without any paywalls or credit cards.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <CalendarIcon className="h-6 w-6 text-primary flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Unified Client CRM</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Access online packages, smart booking calendars, and digital invoices securely.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <UsersIcon className="h-6 w-6 text-primary flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Creative Directory</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Connect with the top visual artists, photographers, models, and creators globally.</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div>
              <button
                onClick={handleRedirect}
                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-150 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {isPortalUser ? "Log In to Your Portal" : "Sign Up & Explore"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DeclinedPage;
